import React, { useState, useEffect, useCallback } from "react";
import apiClient from "../../api/client";
import "./CustomerTransactionHistory.css";

/**
 * Embedded, compact version of History/index.js for a single customer's
 * page — same underlying data model (wallet_history rows tied to a
 * recharge via reference_id), just scoped to one customer_id instead of
 * the requester's whole wallet ledger. No pagination (a single
 * customer's volume is small enough not to need it) and no separate
 * Header (the customer page already has one), but keeps the same date
 * filter since the backend already supports it identically.
 *
 * Columns deliberately differ from the full History page: no Balance
 * After (that's the operator's own wallet state, not something
 * meaningful about what this customer received) and no Remarks —
 * replaced with Plan and Provider from recharge_history, which is what
 * actually answers "what did this customer get recharged".
 */
const CustomerTransactionHistory = ({ customerId }) => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateFilter, setDateFilter] = useState("all");

  const fetchHistory = useCallback(async () => {
    if (!customerId) return;

    setLoading(true);
    setError(null);

    try {
      const { data } = await apiClient.get(
        `/customer/get_customer_wallet_history.php?customer_id=${customerId}&filter=${dateFilter}`
      );

      if (data.success) {
        setTransactions(data.data || []);
      } else {
        throw new Error(data.message || "Unknown error");
      }
    } catch (err) {
      setError(err.message || "Failed to fetch transaction history");
    } finally {
      setLoading(false);
    }
  }, [customerId, dateFilter]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const isCredit = (t) => t.transaction_type?.toLowerCase() === "credit";

  return (
    <div className="customer-txn-history">
      <div className="customer-txn-history-header">
        <h2 className="plan-title" style={{ margin: 0 }}>💰 Transaction History</h2>

        <select
          value={dateFilter}
          onChange={(e) => setDateFilter(e.target.value)}
          className="customer-txn-filter-select"
        >
          <option value="all">All Time</option>
          <option value="today">Today</option>
          <option value="week">This Week</option>
          <option value="month">This Month</option>
        </select>
      </div>

      {loading ? (
        <div className="skeleton-loader">
          {[...Array(4)].map((_, index) => (
            <div key={index} className="skeleton-row">
              <div className="skeleton-cell"></div>
              <div className="skeleton-cell"></div>
              <div className="skeleton-cell"></div>
              <div className="skeleton-cell"></div>
            </div>
          ))}
        </div>
      ) : error ? (
        <div className="customer-txn-error">{error}</div>
      ) : (
        <div className="operator-table-wrapper">
          <table className="operator-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Provider</th>
                <th>Plan</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              {transactions.length > 0 ? (
                transactions.map((t, index) => (
                  <tr
                    key={t.id || `${t.created_at}-${index}`}
                    className={isCredit(t) ? "customer-txn-row-credit" : "customer-txn-row-debit"}
                  >
                    <td>{new Date(t.created_at).toLocaleDateString()}</td>
                    <td>
                      <span className="customer-txn-provider-badge">
                        {t.recharge_type}
                      </span>
                    </td>
                    <td>{t.plan_name || "—"}</td>
                    <td className={isCredit(t) ? "customer-txn-amount-credit" : "customer-txn-amount-debit"}>
                      {isCredit(t) ? "+" : "-"}₹{t.amount}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4" className="no-data">No transactions found for this customer</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default CustomerTransactionHistory;
